import { AUDIO } from '../config/tuning';
import type { AudioEngine } from './AudioEngine';
import { AudioBus } from './AudioEngine';
import { rwrIntervalS, rwrToneHz } from './Spatial';

/**
 * The radar warning receiver's audio (SPEC §3.11, §3.4.1).
 *
 * This is the most important sound in the game. SPEC calls the RWR "the primary threat channel
 * when the picture is degraded" — under heavy jamming the video downlink blocks up and the visual
 * ring goes with it, and the tone is what is left. The M9 acceptance criterion is that the launch
 * tone is *instantly recognisable*, and the note requires all three to be distinguishable with
 * eyes closed.
 *
 * They are therefore separated on three axes at once rather than by volume:
 *
 * | State  | Pitch   | Rhythm            | Timbre                |
 * |--------|---------|-------------------|-----------------------|
 * | Search | 1250 Hz | slow single chirp | clean square          |
 * | Track  |  900 Hz | fast double chirp | warbled (11 Hz FM)    |
 * | Launch | 1650 Hz | continuous        | clean, loud, unbroken |
 *
 * Continuity is what makes the launch tone read as an emergency: every other state is a rhythm the
 * ear can wait through, and this one has no gap to wait for.
 */

export class RwrVoice {
  private readonly audio: AudioEngine;

  private launchOsc: OscillatorNode | null = null;
  private launchGain: GainNode | null = null;
  private warble: OscillatorNode | null = null;
  private warbleGain: GainNode | null = null;

  private built = false;

  /** Seconds until the next pulse of the current state. */
  private nextPulseS = 0;
  private currentState: 'none' | 'search' | 'track' | 'launch' = 'none';

  constructor(audio: AudioEngine) {
    this.audio = audio;
  }

  start(): void {
    if (this.built) return;
    const ctx = this.audio.ctx;
    const bus = this.audio.bus(AudioBus.Rwr);
    if (ctx === null || bus === null) return;

    // The launch tone is held open and gated by gain rather than started on demand: it has to
    // begin the instant a launch is detected, and scheduling an oscillator start costs a frame.
    this.launchOsc = ctx.createOscillator();
    this.launchOsc.type = 'square';
    this.launchOsc.frequency.value = AUDIO.rwrLaunchHz;
    this.launchGain = ctx.createGain();
    this.launchGain.gain.value = 0;
    this.launchOsc.connect(this.launchGain);
    this.launchGain.connect(bus);
    this.launchOsc.start();

    // Warble for the track tone, applied as frequency modulation.
    this.warble = ctx.createOscillator();
    this.warble.frequency.value = AUDIO.rwrTrackWarbleHz;
    this.warbleGain = ctx.createGain();
    this.warbleGain.gain.value = 40;
    this.warble.connect(this.warbleGain);
    this.warble.start();

    this.built = true;
  }

  /**
   * Drive the tones from the worst contact on the ring.
   *
   * Only the most severe state sounds. Layering a search chirp under a launch tone would bury the
   * one that matters, and the RWR's job here is triage, not inventory.
   */
  update(dtS: number, worst: 'none' | 'search' | 'track' | 'launch'): void {
    const ctx = this.audio.ctx;
    if (!this.built || ctx === null) return;
    const now = ctx.currentTime;

    if (worst !== this.currentState) {
      this.currentState = worst;
      // Pulse immediately on a state change so a new track is heard at once rather than up to a
      // whole interval later.
      this.nextPulseS = 0;
    }

    // Launch: continuous, gated on.
    const launchLevel = worst === 'launch' ? AUDIO.rwrLaunchGain : 0;
    this.launchGain?.gain.setTargetAtTime(launchLevel, now, 0.02);

    if (worst === 'none' || worst === 'launch') return;

    this.nextPulseS -= dtS;
    if (this.nextPulseS > 0) return;
    this.nextPulseS = rwrIntervalS(worst);

    this.pulse(worst);
    // The track state is a *double* chirp: two pulses close together is a different rhythm from
    // one, and rhythm survives a bad mix better than pitch does.
    if (worst === 'track') this.pulse(worst, AUDIO.rwrTrackChirpS * 1.6);
  }

  private pulse(state: 'search' | 'track', delayS = 0): void {
    const ctx = this.audio.ctx;
    const bus = this.audio.bus(AudioBus.Rwr);
    if (ctx === null || bus === null) return;

    const start = ctx.currentTime + delayS;
    const duration = state === 'search' ? AUDIO.rwrSearchChirpS : AUDIO.rwrTrackChirpS;

    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = rwrToneHz(state);
    if (state === 'track' && this.warbleGain !== null) {
      this.warbleGain.connect(osc.frequency);
    }

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, start);
    // Short attack and release: a hard-edged click is fatiguing over a twelve-minute mission.
    gain.gain.linearRampToValueAtTime(0.4, start + 0.005);
    gain.gain.setValueAtTime(0.4, start + duration - 0.01);
    gain.gain.linearRampToValueAtTime(0, start + duration);

    osc.connect(gain);
    gain.connect(bus);
    osc.start(start);
    osc.stop(start + duration + 0.02);
    osc.onended = (): void => {
      osc.disconnect();
      gain.disconnect();
    };
  }

  mute(): void {
    const ctx = this.audio.ctx;
    if (ctx === null) return;
    this.launchGain?.gain.setTargetAtTime(0, ctx.currentTime, 0.03);
    this.currentState = 'none';
  }

  dispose(): void {
    try {
      this.launchOsc?.stop();
      this.warble?.stop();
    } catch {
      // Already stopped or the context has closed.
    }
    this.built = false;
  }
}
